import LinkSessionsToPatientModal from '@/components/modal/LinkSessionsToNewPatientModal';
import { toSuggestedMatchPatientProfile } from '@/features/patient-find-or-link/utils/to-suggested-match-patient-profile';
import { useMutateLinkSessionsToPatientProfile } from '@/hooks/mutation/useMutateLinkSessionsToPatientProfile';
import { useQueryPatientProfiles } from '@/hooks/query/useQueryPatientProfiles';
import { useIsMobile } from '@/hooks/useWindowDimensions';
import type { PatientOutcomeSurface } from '@/models/analytics/features/patient';
import { Button } from '@heidi/design-system/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@heidi/design-system/command';
import { Skeleton } from '@heidi/design-system/skeleton';
import {
  TypographyCaption,
  TypographyCaptionBold,
  TypographyH4,
  TypographyP,
} from '@heidi/design-system/typography';
import type { SuggestedPatientMatch } from '@heidi/patients';
import { SuggestedMatchesSection, toSuggestedPatientMatches } from '@heidi/patients';
import { type PatientProfile } from '@heidi/patients/contracts';
import { useCurrentSessionId } from '@heidi/runtime/shell';
import { type PatientProfileAppointmentCandidates } from '@heidi/session/contracts';
import { useQuerySessionById } from '@heidi/session/data';
import { useCommandState } from 'cmdk';
import { isEmpty } from 'lodash-es';
import { Plus } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

export const PatientProfileList = ({
  setOpen,
  commandInputPlaceholder,
  selectedSessionIds,
  disabled = false,
  showSuggestedMatches = false,
  patientProfileAppointmentCandidates,
  suggestedMatches: suggestedMatchesProp,
  trackingSurface,
}: {
  setOpen: (open: boolean) => void;
  commandInputPlaceholder?: string;
  selectedSessionIds: string[];
  disabled?: boolean;
  showSuggestedMatches?: boolean;
  /**
   * @deprecated Suggested matches now link directly from the inline name-only
   * list (APP-7147). No longer used here; the prop and the parent review
   * dialog are retired together in APP-7131.
   */
  onReviewSuggestedMatches?: (matches: readonly SuggestedPatientMatch[]) => void;
  patientProfileAppointmentCandidates?: PatientProfileAppointmentCandidates | null;
  suggestedMatches?: readonly SuggestedPatientMatch[];
  /** `patient_linked` surface attribution; omitted → the event fires bare. */
  trackingSurface?: PatientOutcomeSurface;
}) => {
  const isMobile = useIsMobile();
  const [linkSessionToPatientModalOpen, setLinkSessionToPatientModalOpen] =
    useState(false);
  const onCreateNewPatientClick = () => {
    if (disabled) {
      return;
    }
    setLinkSessionToPatientModalOpen(true);
  };
  const currentSessionId = useCurrentSessionId();
  const { data: patientProfiles, isPending } = useQueryPatientProfiles();
  const { mutate: linkSessionsToPatientProfile } =
    useMutateLinkSessionsToPatientProfile();
  const intl = useIntl();
  const suggestedMatches = useMemo(
    () =>
      suggestedMatchesProp ??
      (showSuggestedMatches
        ? toSuggestedPatientMatches(
            patientProfileAppointmentCandidates,
            patientProfiles,
            intl
          )
        : []),
    [
      intl,
      patientProfileAppointmentCandidates,
      patientProfiles,
      showSuggestedMatches,
      suggestedMatchesProp,
    ]
  );
  const hasSuggestedMatches = showSuggestedMatches && suggestedMatches.length > 0;
  const [value, setValue] = useState('');
  const normalizedSearchQuery = value.trim().toLowerCase();
  const hasSearchQuery = normalizedSearchQuery.length > 0;
  const commandListRef = useRef<HTMLDivElement>(null);

  const handleLinkSessionsToPatientProfile = (patientProfile: PatientProfile) => {
    if (disabled) {
      return;
    }
    // Plain link (related-sessions merge dialog removed in APP-7134).
    linkSessionsToPatientProfile({
      patientProfile,
      sessionIds: selectedSessionIds,
      sessionId: currentSessionId || undefined,
      ...(trackingSurface && { tracking: { surface: trackingSurface } }),
    });
  };

  const { data: session } = useQuerySessionById({
    sessionId: selectedSessionIds.at(0) || '',
    select: (data) => {
      if (!data) return undefined;
      return {
        session_name: data.session_name,
      };
    },
  });
  useEffect(() => {
    if (!hasSuggestedMatches || value.trim()) return;

    const frameId = requestAnimationFrame(() => {
      commandListRef.current?.scrollTo({ top: 0 });
    });

    return () => {
      cancelAnimationFrame(frameId);
    };
  }, [hasSuggestedMatches, isPending, patientProfiles?.length, value]);

  return (
    <>
      <Command className="gap-0.5 rounded-lg p-1">
        <TypographyH4 className="block px-2 sm:hidden">
          <FormattedMessage
            defaultMessage="Link session ‘{sessionName}’"
            id="phCaGKBbki"
            description="Heading for linking the current session to a patient profile"
            values={{
              sessionName:
                session?.session_name ||
                intl.formatMessage({
                  defaultMessage: 'Untitled session',
                  id: 'B8QyTfVlVb',
                  description: 'Fallback name for a session without a title',
                }),
            }}
          />
        </TypographyH4>

        <CommandInput
          placeholder={commandInputPlaceholder}
          className="h-9 py-0"
          value={value}
          onValueChange={setValue}
          showClearButton
          disabled={disabled}
        />
        <Button
          disableAutomaticSvgSizing
          size="xs"
          variant="ghost"
          className="h-fit justify-start px-2 py-1.5 text-text-secondary"
          disabled={disabled}
          onClick={() => {
            onCreateNewPatientClick();
          }}
        >
          <Plus size={16} />
          <TypographyCaption>
            <FormattedMessage
              defaultMessage="Create new patient"
              id="8U9j9r3jQf"
              description="Action to create a patient profile"
            />
          </TypographyCaption>
        </Button>
        <CommandList ref={commandListRef}>
          {hasSuggestedMatches && !hasSearchQuery && (
            <>
              <TypographyCaptionBold className="px-2 pb-1 pt-2">
                <FormattedMessage
                  defaultMessage="Suggested"
                  id="lbeCakWugI"
                  description="Heading for suggested patient matches"
                />
              </TypographyCaptionBold>
              <SuggestedMatchesSection
                matches={suggestedMatches}
                onSelectMatch={(match) => {
                  setOpen(false);
                  handleLinkSessionsToPatientProfile(
                    toSuggestedMatchPatientProfile(match)
                  );
                }}
              />
            </>
          )}
          {isPending ? (
            <CommandGroup className="p-0">
              {[1, 2, 3].map((_, index) => (
                <CommandItem disabled key={index}>
                  <Skeleton className="h-5 w-full" />
                </CommandItem>
              ))}
            </CommandGroup>
          ) : !isEmpty(patientProfiles) ? (
            <>
              <CommandEmpty>
                <FormattedMessage
                  defaultMessage="No results found"
                  id="yaKsKjUifC"
                  description="Empty state when no patient profiles match the search"
                />
              </CommandEmpty>
              <PatientProfileListResultHeading
                hasSuggestedMatches={hasSuggestedMatches}
                hasSearchQuery={hasSearchQuery}
              />
              <CommandGroup className="p-0">
                {patientProfiles?.map((patientProfile, index) => (
                  <CommandItem
                    key={patientProfile.id}
                    value={`${patientProfile.demographic_string}-${index}`}
                    onSelect={() => {
                      setOpen(false);
                      handleLinkSessionsToPatientProfile(patientProfile);
                    }}
                    disabled={disabled}
                  >
                    {isMobile ? (
                      <TypographyP>{patientProfile.demographic_string}</TypographyP>
                    ) : (
                      <TypographyCaption>
                        {patientProfile.demographic_string}
                      </TypographyCaption>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          ) : (
            <div className="flex items-center px-2 py-1.5">
              <TypographyCaption>
                <FormattedMessage
                  defaultMessage="No existing patient profiles"
                  id="ZuEujy2yYX"
                  description="Empty state when the user has no patient profiles"
                />
              </TypographyCaption>
            </div>
          )}
        </CommandList>
      </Command>

      <LinkSessionsToPatientModal
        selectedSessionIds={selectedSessionIds ?? []}
        open={linkSessionToPatientModalOpen}
        setOpen={setLinkSessionToPatientModalOpen}
      />
    </>
  );
};

function PatientProfileListResultHeading({
  hasSuggestedMatches,
  hasSearchQuery,
}: {
  hasSuggestedMatches: boolean;
  hasSearchQuery: boolean;
}) {
  const filteredItemCount = useCommandState((state) => state.filtered.count);
  const hasRenderedPatientResults = !hasSearchQuery || filteredItemCount > 0;

  if (!hasSuggestedMatches || !hasRenderedPatientResults) return null;

  return (
    <TypographyCaptionBold className="px-2 pb-1 pt-2">
      {hasSearchQuery ? (
        <FormattedMessage
          defaultMessage="Suggested"
          id="lbeCakWugI"
          description="Heading for suggested patient matches"
        />
      ) : (
        <FormattedMessage
          defaultMessage="All patients"
          id="RLaTFvuKCe"
          description="Heading for the complete patient profile list"
        />
      )}
    </TypographyCaptionBold>
  );
}
